import React, { useState } from 'react';
import { 
  X, 
  Upload, 
  FileSpreadsheet, 
  Download, 
  Check, 
  AlertCircle, 
  HelpCircle,
  FileText
} from 'lucide-react';
import { ExportService } from '../services/exportService';
import { parseMvSpreadsheetText, parseMvXlsxBuffer, ParsedMvRow } from '../services/importParser';
import { StorageService } from '../services/storage';

interface ImportMvModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  projectName?: string;
  campaignId?: string;
  campaignName?: string;
  onImportComplete: () => void;
}

export const ImportMvModal: React.FC<ImportMvModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  campaignId,
  campaignName,
  onImportComplete,
}) => {
  const activeProjectId = projectId || campaignId || 'kfs-apc-2026';
  const activeProjectName = projectName || campaignName || 'APC Project';
  const [fileName, setFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedMvRow[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setErrorMsg(null);

    if (file.name.toLowerCase().endsWith('.xlsx')) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const buffer = event.target?.result as ArrayBuffer;
          const result = await parseMvXlsxBuffer(buffer);

          if (result.rows.length === 0) {
            setErrorMsg('No valid rows found in file. Please ensure column headers match the template.');
            setParsedRows([]);
          } else {
            setParsedRows(result.rows);
          }
        } catch (err: any) {
          setErrorMsg(`Failed to parse Excel file: ${err.message || 'Unknown format'}`);
          setParsedRows([]);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target?.result as string;
          const result = parseMvSpreadsheetText(text);

          if (result.rows.length === 0) {
            setErrorMsg('No valid rows found in file. Please ensure column headers match the template.');
            setParsedRows([]);
          } else {
            setParsedRows(result.rows);
          }
        } catch (err: any) {
          setErrorMsg(`Failed to parse file: ${err.message || 'Unknown format'}`);
          setParsedRows([]);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleExecuteImport = async () => {
    const validRows = parsedRows.filter((r) => r.isValid);
    if (validRows.length === 0) return;

    setIsImporting(true);
    try {
      await StorageService.importMVs(activeProjectId, validRows);
      onImportComplete();
      onClose();
    } catch (err: any) {
      setErrorMsg(`Error saving imported MVs: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const validCount = parsedRows.filter((r) => r.isValid).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden my-4 sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-sky-600" />
            <h2 className="font-semibold text-slate-900 text-base">
              Import Variables from Excel / CSV
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5 max-h-[82vh] overflow-y-auto">
          {/* Step 1: Download Template */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xs font-semibold text-slate-800">
                  Step 1: Download Pre-Formatted Template
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Use this template containing all required headers (Tag, Description, Controller, Units, Step Size, Notes).
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => ExportService.downloadMvTemplateExcel()}
                  className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium flex items-center gap-1 shadow-xs transition-colors"
                  title="Download native Microsoft Excel template (.xlsx)"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  <span>Excel (.xlsx)</span>
                </button>
                <button
                  type="button"
                  onClick={() => ExportService.downloadMvTemplateCsv()}
                  className="px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 text-xs font-medium flex items-center gap-1 shadow-xs transition-colors"
                  title="Download CSV template (.csv)"
                >
                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                  <span>CSV (.csv)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload File */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-slate-800">
              Step 2: Upload Completed Spreadsheet
            </h3>
            <label className="border-2 border-dashed border-slate-300 hover:border-sky-500 rounded-xl p-5 flex flex-col items-center justify-center cursor-pointer bg-slate-50/50 hover:bg-sky-50/30 transition-colors">
              <Upload className="w-6 h-6 text-slate-400 mb-1.5" />
              <span className="text-xs font-medium text-slate-700">
                {fileName ? fileName : 'Click to select Excel (.xlsx, .xls) or CSV file'}
              </span>
              <span className="text-[11px] text-slate-400 mt-0.5">
                Supports native .xlsx, .csv, .tsv, and .xls spreadsheet formats
              </span>
              <input
                type="file"
                accept=".xlsx,.xls,.csv,.tsv,.txt"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>

          {/* Error Banner */}
          {errorMsg && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Step 3: Preview Table */}
          {parsedRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-800">
                  Step 3: Preview Parsed Variables ({validCount} valid of {parsedRows.length})
                </h3>
                <span className="text-[11px] text-slate-500">
                  Target: {activeProjectName}
                </span>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-mono text-[11px] uppercase sticky top-0">
                      <th className="py-2 px-3">Subcontroller</th>
                      <th className="py-2 px-3">Process Area</th>
                      <th className="py-2 px-3">Tag</th>
                      <th className="py-2 px-3">Description</th>
                      <th className="py-2 px-2">Units</th>
                      <th className="py-2 px-2 text-right">Rec. Step</th>
                      <th className="py-2 px-2 text-right">Appr. Step</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {parsedRows.map((r, i) => (
                      <tr key={i} className={r.isValid ? 'hover:bg-slate-50' : 'bg-rose-50/50'}>
                        <td className="py-1.5 px-3 text-slate-700 whitespace-nowrap">{r.controller}</td>
                        <td className="py-1.5 px-3 text-slate-600 whitespace-nowrap">{r.processArea || '-'}</td>
                        <td className="py-1.5 px-3 font-mono font-bold text-sky-700 whitespace-nowrap">{r.tag}</td>
                        <td className="py-1.5 px-3 text-slate-800 truncate max-w-xs">{r.description}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-500 text-[11px]">{r.units}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-600">{r.recommendedStepSize}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-slate-900 font-semibold">{r.approvedStepSize}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={validCount === 0 || isImporting}
              onClick={handleExecuteImport}
              className={`px-5 py-2 text-xs font-semibold rounded-lg shadow-xs transition-colors flex items-center gap-1.5 ${
                validCount === 0 || isImporting
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                  : 'bg-sky-600 hover:bg-sky-700 text-white'
              }`}
            >
              <Check className="w-3.5 h-3.5" />
              <span>{isImporting ? 'Importing...' : `Import ${validCount} Variables`}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
